import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'

/**
 * A sortable table header cell.
 *
 * Renders a `<th>` whose label is an interactive `button` (so it is keyboard
 * accessible via Enter/Space). Activating the button calls `onToggle(columnKey)`
 * to cycle the table's sort state. A direction indicator is shown when the
 * column is the active sort column (ArrowUp for ascending, ArrowDown for
 * descending) and a faint neutral indicator otherwise. The `<th>` exposes the
 * current sort via `aria-sort`.
 *
 * Supports right alignment for numeric columns and composing with an adjacent
 * help affordance via the `help` slot (rendered beside the sort button).
 *
 * @param {Object} props
 * @param {string} props.columnKey - Stable column id passed to `onToggle`.
 * @param {boolean} props.active - Whether this column is the active sort column.
 * @param {'asc'|'desc'|null} [props.dir] - Active sort direction.
 * @param {'left'|'right'} [props.align] - Header alignment (right for numeric columns).
 * @param {(columnKey: string) => void} props.onToggle - Cycles the sort for this column.
 * @param {React.ReactNode} props.children - Header label content.
 * @param {React.ReactNode} [props.help] - Optional adjacent affordance (e.g. a help icon).
 * @param {string} [props.className] - Extra classes for the `<th>`.
 */
export default function SortableHeader({
  columnKey,
  active,
  dir = null,
  align = 'left',
  onToggle,
  children,
  help,
  className,
}) {
  const isActive = Boolean(active) && (dir === 'asc' || dir === 'desc')
  const ariaSort = isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'

  const thClassName = [
    align === 'right' ? 'text-right' : '',
    'whitespace-nowrap',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const renderIndicator = () => {
    if (isActive) {
      return dir === 'asc' ? (
        <ArrowUp className="h-3 w-3 shrink-0" aria-hidden="true" />
      ) : (
        <ArrowDown className="h-3 w-3 shrink-0" aria-hidden="true" />
      )
    }
    return <ChevronsUpDown className="h-3 w-3 shrink-0 text-muted-foreground/40" aria-hidden="true" />
  }

  return (
    <th className={thClassName} aria-sort={ariaSort}>
      <span
        className={[
          'inline-flex w-full items-center gap-1',
          align === 'right' ? 'justify-end' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          type="button"
          onClick={() => onToggle(columnKey)}
          className="inline-flex items-center gap-1 rounded hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          <span>{children}</span>
          {renderIndicator()}
        </button>
        {help}
      </span>
    </th>
  )
}
